import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig'
import AuthPage from './AuthPage';

// Extracted imports
import { BRAND, storageKey, finishedKey, normalize, difficultyFromMeta } from "./utils/tourUtils";
import { useProgress } from "./hooks/useProgress";
import { Button, Card, Modal } from "./components/ui";
import { MoreMenu, BoxTeaser, UnlockToast, AnswerTrackerModal, ClueCard } from "./components/tour";
import { FinishScreen, IntroLetter, WelcomeSplash } from "./components/intro";
import { radius } from "./utils/designSystem";

// === Tour data constants ===
// TIB_SAN_JUAN is defined below due to smart quote characters in the data
// Image constants used by TIB_SAN_JUAN
const PLACEHOLDER_IMAGE = "https://picsum.photos/1200/800?blur=1";
const STEP1_IMAGE  = "/images/image%201.png";
const STEP2_IMAGE  = "/images/image%202.png";
const STEP3_IMAGE  = "/images/image%203.png";
const STEP4_IMAGE  = "/images/image%204.png";
const STEP5_IMAGE  = "/images/image%205.png";
const STEP6_IMAGE  = "/images/image%206.png";
const STEP7_IMAGE  = "/images/image%207.png";
const STEP8_IMAGE  = "/images/image%208.png";
const STEP9_IMAGE  = "/images/image%209.png";
const STEP10_IMAGE = "/images/image%2010.png";
const STEP11_IMAGE = "/images/image%2011.png";
const STEP12_IMAGE = "/images/image%2012.png";

const TIB_SAN_JUAN = {
  slug: "san-juan-cofresi-treasure",
  title: "San Juan",
  city: "San Juan, Puerto Rico",
  durationMinutes: 120,
  startingPoint: { name: "Cruise Port → Plaza Colón" },
  steps: [
    {
      id: "step-1",
      title: "Clue 1 – Plaza Colón Fountain",
      solution: "SEAHORSE", secretIndex: 6,
      image: STEP1_IMAGE,
      walkTimeMinutes: 3,
      directions:
        '<b>1.</b> From the port, find the blue building marked "Hotel Rumbao." Follow the street on its right, away from the sea.<br><b>2.</b> When a pink building appears on your right, climb the mosaic steps, then turn right.<br><b>3.</b> Follow the bend until you reach a square with a statue.',
      riddle:
        "Half of land and half of sea<br>A creature carved for all to see.<br>With hooves of strength and tail that sways,<br>It guides the ship through ocean ways.",
      hint:
        "Look for an animal that's part horse, part fish in the fountain.",
      history:
        "<b>The Gate Everyone Walked Through.</b> Before this was a plaza, it was a bottleneck. Every soldier, merchant, and slave entering the walled city passed through the Puerta de Santiago, the eastern gate that stood right here. When the walls came down in the 1890s, the gateway became the open square you're standing in.<br><br><b>A Monument of 400 Years.</b> The statue of Christopher Columbus was erected by Spain in 1893 to celebrate the 400th anniversary of his arrival in the Americas. Over time, Puerto Ricans have reframed the plaza as their own, and today it's more about domino games and first dates than colonial glory.<br><br><b>Why Seahorses?</b> The fountain creatures aren't random decoration. In Spanish maritime tradition, the <i>hipocampo</i> (seahorse) was a guardian of sailors. For a city that survived by controlling one of the Caribbean's most strategic harbors, putting seahorses at the entrance was like saying: <i>the sea made us who we are.</i>",
      audioFile: "/audio/san-juan/stop1-sju.mp3",
      answerMeta: "8 letters · easy",
      acceptedAnswers: ["seahorse"],
    },
    {
      id: "step-2",
      title: "Clue 2 – Church of Saint Francis of Assisi",
      solution: "NAVE", secretIndex: 3,
      image: STEP2_IMAGE,
      walkTimeMinutes: 3,
      directions:
        "<b>1.</b> Climb the wide stone staircase behind Plaza Colón, further away from the ocean.<br><b>2.</b> Turn left and walk down the road (Calle San Francisco).<br><b>3.</b> Keep walking until you reach a church on your righthand side. Enter the plaza in front of its entrance.",
      riddle:
        "Within a man’s unyielding gaze,<br>An inscription speaks of bygone days.<br>Once a hall for prayer and song,<br>Find where the faithful stood for so long.",
      hint:
        "Look for an inscription on the ground. It rhymes with “save.”",
      history:
        "<b>The Last Piece Standing.</b> In 1756, Franciscan friars built the original chapel right where the open plaza is now. The building was expanded and rebuilt over the centuries, with the current church finished in 1876. It's the second oldest church in all of Puerto Rico and the only surviving piece of what was once an entire Franciscan convent complex.<br><br><b>Footprints in the plaza.</b> Look down on Plaza San Francisco: stone inlays trace the footprint of the original 18th-century church that extended across what is now open square—an X-ray of the past set into the pavement.<br><br><b>Quiet treasures.</b> Inside are a 1940s painted altar, devotional niches, and a crypt that holds the remains of prominent Puerto Ricans. The church is open to the public during regular hours, except during services.",
      audioFile: "/audio/san-juan/stop2-sju.mp3",
      answerMeta: "4 letters · medium",
      acceptedAnswers: ["nave"],
    },
    {
      id: "step-3",
      title: "Clue 3 – Castillo San Cristóbal",
      solution: "STAR", secretIndex: 2,
      image: STEP3_IMAGE,
      walkTimeMinutes: 6,
      directions:
        "<b>1.</b> With the entrance of the church to your back, walk forward and take the grand staircase to your right.<br><b>2.</b> Climb to the top, then take a right and follow the street.<br><b>3.</b> Keep walking until you reach the fortress up ahead.",
      riddle:
        "A triplet up high, they twist and sway,<br>Telling tales of yesterday.<br>Two hold stripes, so bold and so true,<br>But one alone lacks this shape to view.",
      hint:
        "This shape is on the U.S. and Puerto Rico flags.",
      history:
        "<b>The landward shield.</b> Begun in 1634 and completed in 1783, San Cristóbal is the largest Spanish fortress in the Americas. Its mission was to stop attackers approaching by land before they could threaten the city or El Morro across the bay mouth.<br><br><b>Built to Break an Attack.</b> The fort uses a defense in depth system, with five independent structures connected by tunnels and dry moats. An attacker who breached the outer wall would face another wall behind it, then another. Soldiers could retreat inward while continuing to fight, and underground tunnels let them move between positions without being exposed to enemy fire.<br><br><b>Battle Tested.</b> In 1797, a British fleet of 60 ships and roughly 7,000 troops attacked San Juan. San Cristóbal's guns helped hold the line for two weeks until the British withdrew. It was the last major foreign assault on the city, and the fort never fell to an enemy.",
      audioFile: "/audio/san-juan/stop3-sju.mp3",
      answerMeta: "4 letters · medium",
      acceptedAnswers: ["star"],
    },
    {
      id: "step-4",
      title: "Clue 4 – La Perla Overlook",
      solution: "PEARL", secretIndex: 3,
      image: STEP4_IMAGE,
      walkTimeMinutes: 4,
      directions:
        "<b>1.</b> From the fortress, turn around to place it behind you.<br><b>2.</b> Follow the road that goes along the ocean’s edge.<br><b>3.</b> Keep walking until you reach a sign for La Perla on your righthand side. Admire La Perla from above as entering the neighborhood is not part of this tour.",
      riddle:
        "Not handmade, yet prized the same,<br>It lends the area its treasured name.<br>It’s round, it’s rare, it holds great worth,<br>What name reflects its ocean birth?",
      hint: "This round ocean treasure is formed in shells.",
      history:
        "<b>Born outside the walls.</b> La Perla took shape in the 18th century beyond the protective city gates—first as an area for trades considered undesirable inside the walls, later as a working-class quarter on the waves.<br><br><b>Color, Culture, Community.</b> Over generations, residents transformed La Perla into a visually striking neighborhood with vivid facades, murals, and street art. It has its own plazas, basketball courts, and even a small beach. It also served as the backdrop for Luis Fonsi's 2017 \"Despacito\" music video, one of the most-viewed videos in YouTube history.<br><br><b>Look, but from Here.</b> We recommend admiring La Perla from this vantage point rather than walking in. While the neighborhood has evolved in recent years, it still carries real safety considerations for visitors unfamiliar with the area. If you do choose to enter, go during daylight, stick to main paths, and respect residents' privacy.",
      audioFile: "/audio/san-juan/stop4-sju.mp3",
      answerMeta: "5 letters · easy",
      acceptedAnswers: ["pearl"],
    },
    {
      id: "step-5",
      title: "Clue 5 – Callejón de la Puerta con la Bandera",
      solution: "CUBA", secretIndex: 0,
      image: STEP5_IMAGE,
      walkTimeMinutes: 5,
      directions:
        "<b>1.</b> Continue on the same road, with the sea to your right.<br><b>2.</b> Follow it uphill, avoiding the down ramp on the right.<br><b>3.</b> Walk past grassy patches on your right.<br><b>4.</b> After you pass the graffiti wall on your left, take a left down the alley with a large flag mural in view.",
      riddle:
        "This flag you see, in red, white, and blue,<br>Feels familiar, and here's a clue:<br>Just swap the red and blue around,<br>The twin you seek is southward bound.",
      hint: "This Caribbean country is known for cigars and classic cars.",
      history:
        "<b>The famous flag door.</b> In 2012, local artists transformed a weathered doorway with Puerto Rico’s flag, turning an abandoned house into a cultural landmark. Visitors have queued for photos ever since.<br><br><b>Colors with meaning.</b> The five stripes (three red, two white) symbolize the blood and sacrifice that sustain liberty and the balance of people and government; the blue triangle evokes the republican government and surrounding sea and sky; the lone white star stands for Puerto Rico itself.<br><br><b>A Caribbean echo.</b> Puerto Rico’s flag mirrors Cuba’s—same layout, inverted colors—reflecting 19th-century solidarity between reform movements on both islands.",
      audioFile: "/audio/san-juan/stop5-sju.mp3",
      answerMeta: "4 letters · hard",
      acceptedAnswers: ["cuba"],
    },
    {
      id: "step-6",
      title: "Clue 6 – Plaza del Quinto Centenario",
      solution: "FIFTH", secretIndex: 4,
      image: STEP6_IMAGE,
      walkTimeMinutes: 4,
      directions:
        "<b>1.</b> Continue along the road that brought you to the flag mural. You will eventually return to the seaside street.<br><b>2.</b> Take a left, then continue along the road until a plaza with green light poles appears on your left.<br><b>3.</b> Walk to the square with the towering column.",
      riddle:
        "Now look around this spacious square,<br>Where a column stands in the open air.<br>A plaza named for centuries long sifted,<br>Which in the count would this be listed?",
      hint: "The pinky’s position when you count from the thumb.",
      history:
        "<b>500 years later.</b> Opened in 1992, the Plaza del Quinto Centenario commemorates five centuries since 1492 and the profound—often painful—meeting of Indigenous, African, and European worlds in the Caribbean.<br><br><b>Tótem Telúrico.</b> The plaza’s 40-foot ceramic column by Jaime Suárez incorporates shattered clay and Taíno-inspired motifs, suggesting the ground itself remembers. It rises like an archaeological core sample pulled from Puerto Rican soil.<br><br><b>Layers underfoot.</b> The site overlays a former Dominican monastery and, later, Spanish military installations, compressing centuries of faith, empire, and everyday life into one public square.",
      audioFile: "/audio/san-juan/stop6-sju.mp3",
      answerMeta: "5 letters · medium",
      acceptedAnswers: ["fifth"],
    },
    {
      id: "step-7",
      title: "Clue 7 – El Morro (Lawn/Lighthouse View)",
      solution: "LIGHTHOUSE", secretIndex: 4,
      image: STEP7_IMAGE,
      walkTimeMinutes: 3,
      directions:
        "<b>1.</b> From the plaza, walk back toward the ocean in sight.<br><b>2.</b> Follow the road to the left lined with palm trees; you will see El Morro fort on your righthand side.<br><b>3.</b> Continue to where the paved road becomes a wide lawn.",
      riddle:
        "It crowns the cliffs with quiet might,<br>A turning eye that cuts the night.<br>No voice, no flame, yet still it warns,<br>What keeps the ships from ocean’s thorns?",
      hint: "A glowing guide on shore that says: danger’s near!",
      history:
        "<b>Guardian of the Bay.</b> Construction of El Morro began in 1539 in a strategic position that controls the entrance to San Juan Bay. Over the next 250 years, Spain expanded it from a simple gun platform into a six-level citadel. The small domed sentry boxes at its corners, called garitas, have become one of the most recognized symbols of Puerto Rico.<br><br><b>Attacked but Never Taken.</b> El Morro faced some of the most powerful navies in the world. Sir Francis Drake tried and failed to breach it in 1595. The Dutch occupied parts of San Juan in 1625 but could not hold the fort. In 1797, a British fleet besieged the city for two weeks before withdrawing. And in 1898, the U.S. Navy bombarded it during the Spanish-American War. Through it all, the fort was never captured by force.<br><br><b>The View from Here.</b> Take in the full profile of the fortress and the lighthouse that crowns its ramparts. The lighthouse was first built by Spain in 1846, destroyed during the 1898 bombardment, and rebuilt in 1908. It still flashes every 15 seconds to guide ships into port. On any given day, you will see locals flying kites on the lawn, one of the best breezes in the city.",
      audioFile: "/audio/san-juan/stop7-sju.mp3",
      answerMeta: "10 letters · easy",
      acceptedAnswers: ["lighthouse"],
    },
    {
      id: "step-8",
      title: "Clue 8 – La Rogativa",
      solution: "TORCHES", secretIndex: 4,
      image: STEP8_IMAGE,
      walkTimeMinutes: 6,
      directions:
        "<b>1.</b> With your back to El Morro, find the yellow buildings with a red dome.<br><b>2.</b> Follow the road to their right.<br><b>3.</b> This will guide you along an oceanside street. Walk until you reach a statue with four bronze figures.",
      riddle:
        "They came with prayers in midnight's breath,<br>To fend off fear, to ward off death.<br>What lit their way, defied all doubt,<br>These flames of hope, they carried out?",
      hint:
        "Not candles or lanterns — fiery sticks starting with “T”.",
      history:
        "<b>Legend on the ramparts.</b> Sculptor Lindsay Daen’s 1971 bronze recalls the night of April 30, 1797, when the bishop of San Juan, accompanied by women with torches and ringing bells, processed through the city to plead for deliverance during a British siege.<br><br><b>Fear, fog, and retreat.</b> According to tradition, the flickering procession was mistaken for reinforcements, unnerving the attackers and helping end the assault. Whether literal history or patriotic lore, the story captures the city’s spirit of resilience.<br><br><b>A pause with a view.</b> The overlook peers across the city wall to the Atlantic—quiet now, but once a theater of watchfires and warning shots.",
      audioFile: "/audio/san-juan/stop8-sju.mp3",
      answerMeta: "7 letters · medium",
      acceptedAnswers: ["torches"],
    },
    {
      id: "step-9",
      title: "Clue 9 – Catedral de San Juan Bautista",
      solution: "POPE", secretIndex: 3,
      image: STEP9_IMAGE,
      walkTimeMinutes: 3,
      directions:
        "<b>1.</b> From the statue, continue down the same street.<br><b>2.</b> It will begin to take you uphill away from the ocean.<br><b>3.</b> Follow this road up until you reach the Cathedral de San Juan.",
      riddle:
        "The bishop’s home, a place of prayer,<br>Is named by someone far from there.<br>From Vatican halls, his word has weight,<br>Who gave this church its special state?",
      hint: "The leader of the Catholic Church.",
      history:
        "<b>Oldest on U.S. soil.</b> Founded in 1521 and rebuilt after storms and time, this is one of the oldest cathedrals in the Americas and the oldest church building in continuous use on U.S. soil.<br><br><b>Ponce de Leon's Final Resting Place.</b> Inside lies the marble tomb of Juan Ponce de Leon, the Spanish explorer who served as Puerto Rico's first governor. The cathedral also houses a national shrine to Our Lady of Divine Providence, Puerto Rico's patron saint, and relics of Blessed Carlos Manuel Rodriguez, the first Caribbean-born layperson to be beatified by the Catholic Church.<br><br><b>Built and Rebuilt.</b> The exterior blends centuries of repair and renovation. Gothic pointed arches sit alongside neoclassical columns, and quiet side chapels reflect different eras of devotion. Even from outside, the pale facade and rounded bell tower are among the most photographed corners of Old San Juan.",
      audioFile: "/audio/san-juan/stop9-sju.mp3",
      answerMeta: "4 letters · easy",
      acceptedAnswers: ["pope"],
    },
    {
      id: "step-10",
      title: "Clue 10 – Puerta de San Juan",
      solution: "BAPTIST", secretIndex: 0,
      image: STEP10_IMAGE,
      walkTimeMinutes: 4,
      directions:
        "<b>1.</b> From the church, start heading back downhill, but on the other street next to the one you just came up. Facing downhill from the church, this is the street on the left.<br><b>2.</b> Continue walking until you reach the old gate.<br><b>3.</b> Solve the clue before passing through.",
      riddle:
        "This gateway honors Saintly past,<br>With painted tile and stone that lasts.<br>Look above, then ask yourself:<br>Which title names Saint John himself?",
      hint: "The placard nearby tells of “John the _______”.",
      history:
        "<b>The sea gate.</b> Built in 1635, the Puerta de San Juan is the last surviving of the five gates that once closed nightly to secure the walled city. Sailors, merchants, and dignitaries entered here from ships at anchor below.<br><br><b>Words of welcome.</b> The Latin inscription above—“Benedictus qui venit in nomine Domini” (“Blessed is he who comes in the name of the Lord”)—set the tone for travelers climbing to give thanks at the cathedral.<br><br><b>Crimson and stone.</b> The gate’s bold red and massive masonry make it one of Old San Juan’s most iconic backdrops, a perfect frame for the bay just beyond.",
      audioFile: "/audio/san-juan/stop10-sju.mp3",
      answerMeta: "7 letters · hard",
      acceptedAnswers: ["baptist"],
    },
    {
      id: "step-11",
      title: "Clue 11 – Fuente Raíces",
      solution: "ROOTS", secretIndex: 1,
      image: STEP11_IMAGE,
      walkTimeMinutes: 3,
      directions:
        "<b>1.</b> Walk through the gate and continue along the oceanside path.<br><b>2.</b> Keep the water to your right as you walk.<br><b>3.</b> Continue until you come across a fountain with figures rising from its flowing base.",
      riddle:
        "Three figures rise where waters play,<br>From lands and cultures far away.<br>Their stories join where journeys meet,<br>What lies beneath to make them complete?",
      hint:
        "Hidden below, they hold trees and history in place.",
      history:
        "<b>Origins in bronze and spray.</b> Unveiled in 1992 for the quincentennial, the Fuente Raíces (“Roots Fountain”) animates the tip of Paseo de la Princesa with a dramatic tableau of rearing horses, dolphins, Taíno caciques, African figures, Spaniards—and the sea itself.<br><br><b>Three great strands.</b> The work by architect Miguel A. Carlo and sculptor Luis A. Sanguino celebrates the island’s three ancestral pillars—Taíno, African, and Spanish—whose intertwining shaped Puerto Rico’s language, music, faith, and food.<br><br><b>Harbor theater.</b> Stand just so and the statues resemble a ship surging into the bay, with water jets for surf and the city walls for sails.",
      audioFile: "/audio/san-juan/stop11-sju.mp3",
      answerMeta: "5 letters · hard",
      acceptedAnswers: ["roots"],
    },
    {
      id: "step-12",
      title: "Clue 12 – La Casita de Rones / Plaza Dársenas",
      solution: "X", secretIndex: 0,
      image: STEP12_IMAGE,
      walkTimeMinutes: 5,
      directions:
        "<b>1.</b> Follow the promenade back toward town until it ends.<br><b>2.</b> Take a right towards the sea, then turn left; walk along the water in the direction of the cruise port.<br><b>3.</b> A pink house and fountain will come into view — this is where you’ll find our final clue.",
      riddle:
        "Beside the house where rums are found,<br>Culebra’s crest is found on ground.<br>Two crossing staffs in proud array,<br>What single letter do they portray?",
      hint: "Find the “Culebra” mosaic; this letter marks the spot…",
      history:
        "<b>Rum island.</b> Puerto Rico has distilled rum since the 1500s, turning sugar-cane molasses into a spirit that would become the island’s signature export. Today a large share of rum sold in the U.S. is Puerto Rican, with brands like Don Q and Bacardí aging spirits in oak for smooth, vanilla-spiced character.<br><br><b>Little pink welcome.</b> La Casita de Rones (“The Little House of Rums”), built in 1937 as a waterfront pavilion and revived in 2016, serves as the island’s informal rum embassy—tastings, cocktails, and shelves lined with local labels.<br><br><b>Harbor crossroads.</b> Plaza Dársenas fronts the ferry docks and cruise piers; look for the mosaic medallions of Puerto Rican islands—Vieques, Culebra, and more—set into the pavement like a sailor’s map.",
      audioFile: "/audio/san-juan/stop12-sju.mp3",
      answerMeta: "1 letter · hard",
      acceptedAnswers: ["x"],
    },
  ],
};
// === End constants ===
























function AuthedApp() {

  const tour = useMemo(() => TIB_SAN_JUAN, []);
  const phaseKey = `tib:${tour.slug}:phase`;
  const [phase, setPhase] = useState(() => {
    try {
      const saved = localStorage.getItem(phaseKey);
      if (saved) return saved;
      const raw = localStorage.getItem(storageKey(tour.slug));
      if (raw) {
        const p = JSON.parse(raw);
        if ((p.index ?? 0) > 0 || (p.answers && Object.keys(p.answers).length > 0)) return 'tour';
      }
    } catch {}
    return 'welcome';
  });
  const [finished, setFinished] = useState(() => localStorage.getItem(finishedKey(tour.slug)) === "true");
  const [showHelp, setShowHelp] = useState(false);
  const [showTracker, setShowTracker] = useState(false);
  const [justSolvedId, setJustSolvedId] = useState(null);
  const [showStreakPopover, setShowStreakPopover] = useState(false);
  const [streakPopoverPos, setStreakPopoverPos] = useState({ top: 0, left: 0 });
  const streakButtonRef = React.useRef(null);
  const lastShownRef = React.useRef(null);

  const { current, progress, isRevealed, reveal, submitAnswer, isAnsweredCorrect, next, prev, goto, reset, getLetterMask, revealNextLetter, streak } = useProgress(
    tour.slug,
    tour.steps
  );

  const _scrollToTop = () => {
    try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch (e) { try { window.scrollTo(0, 0); } catch {} }
    try {
      const mainEl = document.querySelector('main');
      if (mainEl && typeof mainEl.scrollTo === 'function') mainEl.scrollTo(0, 0);
    } catch {}
  };

  useEffect(() => { _scrollToTop(); }, [progress.index]);
  useEffect(() => { if (phase === 'tour') _scrollToTop(); }, [phase]);

  useEffect(() => {
    try { localStorage.setItem(phaseKey, phase); } catch {}
  }, [phase, phaseKey]);

  useEffect(() => {
    const hrefs = [
      "https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap&v=2",
      "https://fonts.googleapis.com/css2?family=Caveat:wght@600&family=Special+Elite&display=swap&v=2",
      "https://fonts.googleapis.com/css2?family=Special+Elite&display=swap&v=2",
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap&v=2",
    ];
    hrefs.forEach((href) => {
      if (!document.querySelector(`link[href='${href}']`)) {
        const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = href; document.head.appendChild(link);
      }
    });
  }, []);

  const allSolved = useMemo(() => (
    tour.steps.every((s) => Array.isArray(s.acceptedAnswers) && s.acceptedAnswers.includes(progress.answers?.[s.id]))
  ), [progress.answers, tour.steps]);
  const [showUnlockToast, setShowUnlockToast] = useState(false);
  const wasAllSolvedRef = useRef(false);

  useEffect(() => {
    if (allSolved && !wasAllSolvedRef.current) {
      setShowUnlockToast(true);
      try { navigator.vibrate && navigator.vibrate([50, 50, 50]); } catch {}

      const t = setTimeout(() => setShowUnlockToast(false), 1000000);
      return () => clearTimeout(t);
    }
    wasAllSolvedRef.current = allSolved;
  }, [allSolved]);

  const correct = isAnsweredCorrect(current);

  const solvedCount = useMemo(() => tour.steps.filter((s) => s.acceptedAnswers.includes(progress.answers?.[s.id])).length, [tour.steps, progress.answers]);

  useEffect(() => {
    const solvedNow = isAnsweredCorrect(current);
    const latestAns = progress.answers?.[current.id];
    if (solvedNow && latestAns && lastShownRef.current !== latestAns) {
      setJustSolvedId(current.id);
      setShowTracker(true);
      lastShownRef.current = latestAns;
    }
  }, [current.id, progress.answers, isAnsweredCorrect, current]);

  useEffect(() => {
    try {
      console.assert(typeof useState === 'function' && typeof useEffect === 'function', 'React hooks are available');
      console.assert(storageKey('slug') === 'tib_progress_slug', 'storageKey formats correctly');
      console.assert(normalize(' Sea-horse! ') === 'seahorse', 'normalize strips non-alphanumerics');
      console.assert(Array.isArray(TIB_SAN_JUAN.steps) && TIB_SAN_JUAN.steps.length === 12, '12 steps exist');
      const __q = 'Plaza Colón Fountain'; const __enc = encodeURIComponent(__q);
      console.assert(`https://www.google.com/maps/search/?api=1&query=${__enc}`.includes(__enc), 'maps query is encoded');
      const __d1 = difficultyFromMeta('8 letters · easy'); const __d2 = difficultyFromMeta('4 letters · medium'); const __d3 = difficultyFromMeta('1 letter · hard');
      console.assert(__d1.level === 1 && __d1.label === 'Easy'); console.assert(__d2.level === 2 && __d2.label === 'Medium'); console.assert(__d3.level === 3 && __d3.label === 'Hard');
      console.assert(TIB_SAN_JUAN.steps[0].acceptedAnswers.includes('seahorse'), 'step 1 accepted answer includes "seahorse"');
      console.assert(typeof TIB_SAN_JUAN.steps[0].image === 'string' && /^https?:/.test(TIB_SAN_JUAN.steps[0].image), 'step 1 image is a URL string');
      console.assert(typeof Button === 'function' && typeof Card === 'function' && typeof Modal === 'function', 'UI primitives exist');
      console.assert(typeof WelcomeSplash === 'function' && typeof IntroLetter === 'function' && typeof ClueCard === 'function', 'core components exist');
      console.assert(typeof next === 'function' && typeof prev === 'function' && typeof goto === 'function', 'nav functions exist');
      console.assert(TIB_SAN_JUAN.steps[11].acceptedAnswers.includes('x'), 'last step accepts x');
      console.assert(/^https?:/.test(BRAND.logoUrl), 'BRAND.logoUrl is a URL');
      console.assert(TIB_SAN_JUAN.steps[4].acceptedAnswers.includes('cuba') && TIB_SAN_JUAN.steps[8].acceptedAnswers.includes('pope'), 'answers for steps 5 and 9 present');
      console.assert("A\n\nB".split(/\n\n/).length === 2, 'Typewriter double-newline split works');

      console.assert(difficultyFromMeta('Medium challenge').level === 2, 'difficulty meta parsing (medium)');
      console.assert('Café!'.normalize ? true : true, 'env supports unicode strings');
      console.assert(normalize('Café!') === 'caf', 'normalize removes diacritics and punctuation');
      const __punct = /[.!?,]/; console.assert(__punct.test('!') && !__punct.test('a'), 'punctuation regex sanity');
    } catch {}
  }, [allSolved, next, prev, goto]);

  const handleRestart = () => {
    localStorage.removeItem(finishedKey(tour.slug));
    localStorage.removeItem(storageKey(tour.slug));
    setFinished(false); setPhase('welcome'); reset(); window.scrollTo(0, 0);
  };

  if (finished) return <FinishScreen tourSlug={tour.slug} onRestart={handleRestart} />;
  const handleStart = () => { setPhase('letter'); };
  if (phase === 'welcome') return <WelcomeSplash onStart={handleStart} />;
  if (phase === 'letter') return <IntroLetter slug={tour.slug} onBegin={() => setPhase('tour')} />;

  const atEnd = progress.index >= tour.steps.length - 1;

  return (
    <div className="route-fade min-h-screen bg-coastal text-slate-900 font-body">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600&family=Special+Elite&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
        .bg-coastal {
background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
background-attachment: fixed;
}
        .glass-card {
background: rgba(255, 255, 255, 0.7);
backdrop-filter: blur(12px);
-webkit-backdrop-filter: blur(12px);
border-radius: 20px;
box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}
        .brass-button { background: linear-gradient(180deg, #B35C3F 0%, #9E4E36 100%); color: #ffffff; border: 0; box-shadow: 0 6px 14px rgba(179,92,63,0.35); }
        .black-button { background: #000; color: #fff; border: 0; box-shadow: 0 3px 8px rgba(0,0,0,0.18); }
        .black-button:hover { box-shadow: 0 0 0 3px rgba(255,255,255,0.2), 0 8px 22px rgba(0,0,0,0.35); transform: translateY(-1px); }
        .route-fade { animation: routeFade 420ms cubic-bezier(.2,.7,.2,1); }
        @keyframes routeFade { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: translateY(0); } }
        .slide-in { animation: slideIn 380ms cubic-bezier(.2,.8,.2,1); }
        @keyframes slideIn { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: translateY(0); } }
        .font-libre { font-family: 'Crimson Pro', Georgia, serif; }
        .font-body { font-family: 'Plus Jakarta Sans', Inter, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, sans-serif; }
        .fade-in-letter { opacity: 0; animation: fadeIn 420ms ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {}
      <header className="z-10 border-b border-white/30 bg-white/50 backdrop-blur-md" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="mx-auto w-full max-w-xl px-4 py-3">
          <div className="flex items-center gap-3 justify-between">
            {}
            <div className="flex items-center gap-3 min-w-0">
              {BRAND.logoUrl ? (
                <img src={BRAND.logoUrl} alt={BRAND.name} className="h-6 w-6 sm:h-8 sm:w-8 shrink-0" />
              ) : (
                <div className="grid h-6 w-6 sm:h-8 sm:w-8 place-items-center rounded-md text-xs font-bold text-white shrink-0" style={{ background: '#000' }}>📦</div>
              )}
              <div className="min-w-0">
                <div className="text-[9px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 hidden xs:block">TOUR IN A BOX</div>
                <div className="flex items-center gap-2">
                  <div className="truncate font-libre font-bold text-base sm:text-lg" style={{ color: 'black' }}>{tour.title}</div>
                  {streak >= 2 && (
                    <>
                      <button
                        ref={streakButtonRef}
                        onClick={() => {
                          if (!showStreakPopover && streakButtonRef.current) {
                            const rect = streakButtonRef.current.getBoundingClientRect();
                            setStreakPopoverPos({
                              top: rect.bottom + 8,
                              left: rect.left + rect.width / 2
                            });
                          }
                          setShowStreakPopover(!showStreakPopover);
                        }}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full shrink-0 cursor-pointer transition-transform active:scale-95"
                        style={{
                          background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                          boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                          border: 'none'
                        }}
                        aria-label={`${streak} correct answers in a row - click for details`}
                      >
                        <span className="text-sm">🔥</span>
                        <span className="text-xs font-bold text-white">{streak}</span>
                      </button>

                      {showStreakPopover && ReactDOM.createPortal(
                        <>
                          {/* Backdrop to close on outside click */}
                          <div
                            className="fixed inset-0"
                            onClick={() => setShowStreakPopover(false)}
                            style={{ background: 'transparent', zIndex: 9998 }}
                          />

                          {/* Popover - using fixed positioning and portal */}
                          <div
                            className="fixed w-64 p-3 rounded-lg shadow-lg"
                            style={{
                              top: `${streakPopoverPos.top}px`,
                              left: `${streakPopoverPos.left}px`,
                              transform: 'translateX(-50%)',
                              background: 'white',
                              border: '1px solid #E5E7EB',
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                              zIndex: 9999
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xl">🔥</span>
                              <h3 className="font-bold text-sm" style={{ color: '#111827' }}>Answer Streak</h3>
                            </div>
                            <p className="text-xs leading-relaxed mb-2" style={{ color: '#4B5563' }}>
                              You're on a <strong>{streak}-location streak</strong>! Keep getting answers correct on your first try to maintain it.
                            </p>
                            <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>
                              Wrong answers reset your streak to zero, even if you eventually solve it.
                            </p>
                            {/* Arrow pointing up */}
                            <div
                              className="absolute"
                              style={{
                                top: '-8px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderBottom: '8px solid white',
                                filter: 'drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.05))'
                              }}
                            />
                          </div>
                        </>,
                        document.body
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {}
            <div className="flex items-center gap-2 flex-none">
              <button
                className="h-9 w-9 sm:h-auto sm:w-auto text-sm font-semibold rounded-md ring-1 ring-black/5 sm:ring-0 disabled:opacity-40"
                onClick={prev}
                disabled={progress.index === 0}
                title="Previous"
              >
                ←<span className="hidden sm:inline">&nbsp;Prev</span>
              </button>
              <button
                className="h-9 w-9 sm:h-auto sm:w-auto text-sm font-semibold rounded-md ring-1 ring-black/5 sm:ring-0"
                onClick={next}
                title="Next"
              >
                <span className="hidden sm:inline">Next&nbsp;</span>→
              </button>
            </div>

            {}
            <div className="flex items-center gap-2 flex-none">
              <MoreMenu
                onHelp={() => setShowHelp(true)}
                onTracker={() => setShowTracker(true)}
                onReset={() => { if (window.confirm('Reset your progress?')) { handleRestart(); } }}
              />
              <BoxTeaser
                unlocked={allSolved}
                onOpen={() => { localStorage.setItem(finishedKey(tour.slug), "true"); setFinished(true); }}
              />
            </div>
          </div>
        </div>
      </header>
      <UnlockToast
        open={showUnlockToast}
        onOpen={() => { localStorage.setItem(finishedKey(tour.slug), "true"); setFinished(true); setShowUnlockToast(false); }}
        onClose={() => setShowUnlockToast(false)}
      />

{}
      <Modal open={showHelp} onClose={() => setShowHelp(false)} title="How to Play">
        <p className="text-slate-800 mb-3">Welcome to San Juan! These instructions explain how to use your tour and can be revisited anytime by tapping Help.</p>
        <ul className="space-y-3 text-slate-800">
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>1</span>
            <div><span className="font-semibold">Start at the Cruise Port</span> – Our adventure begins at Old San Juan Cruise Port, right by Hotel Rumbao.<a href="https://www.google.com/maps/search/?api=1&query=Hotel%20Rumbao%2C%20San%20Juan%2C%20Puerto%20Rico" target="_blank" rel="noreferrer" className="ml-2 text-sm underline text-slate-700">Find on Map</a></div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>2</span>
            <div><span className="font-semibold">Follow the Trail</span> – You’ll visit 12 iconic spots in order, each with built-in directions.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>3</span>
            <div><span className="font-semibold">Solve the Riddle</span> – At every stop, open Cofresí’s Clue, read the riddle, and enter your answer. Need help? Use the Hint button.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>4</span>
            <div><span className="font-semibold">Explore</span> – Expand "History & Nearby" to uncover the history and discover local recommendations.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>5</span>
            <div><span className="font-semibold">Track Your Progress</span> – Use the Answer Tracker to follow your progress and collect letters along the way. When all 12 riddles are solved, the final reward will be yours to uncover.</div>
          </li>
        </ul>
      </Modal>

            {}
      <div className="mx-auto max-w-xl px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex flex-1 items-center gap-2 flex-nowrap min-w-0">
            {tour.steps.map((s, idx) => {
              const solved = Array.isArray(s.acceptedAnswers) && s.acceptedAnswers.includes(progress.answers?.[s.id]);
              const isCurrent = idx === progress.index;
              return (
                <div
                  key={s.id}
                  className="relative h-2 flex-1 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: solved ? '#10B981' : '#E5E7EB',
                    boxShadow: isCurrent ? '0 0 0 2px #FFF, 0 0 0 4px #10B981' : 'none',
                  }}
                  title={`Clue ${idx + 1} ${solved ? 'solved' : 'not solved'}${isCurrent ? ' (current)' : ''}`}
                  aria-label={`Clue ${idx + 1} ${solved ? 'solved' : 'not solved'}${isCurrent ? ' (current)' : ''}`}
                />
              );
            })}
          </div>
          <div className="text-xs font-semibold whitespace-nowrap flex-none" style={{ color: '#6B7280' }}>{solvedCount}/{tour.steps.length}</div>
        </div>
      </div>

      {}
      <main className="mx-auto max-w-xl space-y-6 px-4 pb-28 pt-2">
        {}
          <Card padding="none" className="overflow-hidden">
            {current.image && (
              <div className="relative overflow-hidden" style={{ borderRadius: radius.card }}>
                <img
                  src={current.image || PLACEHOLDER_IMAGE}
                  alt={`Location ${progress.index + 1}`}
                  className="h-80 md:h-96 w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 768px) 100vw, 768px"
                  style={{
                    animation: 'fadeIn 0.5s ease-out'
                  }}
                />
                {/* Frosted glass overlay card */}
                <div
                  className="absolute bottom-0 left-0 right-0 p-5 md:p-6"
                  style={{
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                    borderBottomLeftRadius: radius.card,
                    borderBottomRightRadius: radius.card,
                  }}
                >
                  <div className="space-y-1">
                    <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                      Location {progress.index + 1} of {tour.steps.length}
                    </div>
                    <div
                      className="font-libre text-2xl md:text-3xl font-extrabold leading-tight"
                      style={{ color: 'black' }}
                    >
                      {(() => {
                        const t = current.title || '';
                        const i = t.indexOf('–');
                        const lower = t.toLowerCase();
                        return lower.startsWith('clue ') && i > -1
                          ? t.slice(i + 1).trim()
                          : t;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

        <div key={current.id} className="slide-in">
          <ClueCard
            step={current}
            isRevealed={isRevealed}
            reveal={reveal}
            isCorrect={correct}
            onSubmit={(val) => submitAnswer(current.id, val)}
            letterMask={getLetterMask(current)}
            onRevealLetter={() => revealNextLetter(current)}
            badgeColor="#F8662B"
          />
        </div>
      </main>

      {}
      <footer className="fixed inset-x-0 bottom-0 border-t border-white/30 bg-white/60 backdrop-blur-md" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="mx-auto max-w-xl items-center gap-3 px-4 py-3">
          {!atEnd && (
            <div className="py-2">
              <Button variant="black" onClick={next} disabled={correct !== true}>Next Location →</Button>
            </div>
          )}
        </div>
      </footer>
      {}
      <AnswerTrackerModal open={showTracker} onClose={() => setShowTracker(false)} tour={tour} progress={progress} justSolvedId={justSolvedId} />

    </div>
  );
}
  <style>{`
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: scale(1.02);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    .letter-card .black-pill-btn {
      font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial !important;
      letter-spacing: 0 !important;
    }
  `}</style>

export default function App() {
  const [authStatus, setAuthStatus] = useState("checking");
  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        setAuthUser(fbUser);
        setAuthStatus("in");
      } else {
        setAuthUser(null);
        setAuthStatus("out");
      }
    });
    return unsub;
  }, []);

  if (authStatus === "checking") {
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">
          Checking your session…
        </div>
      </div>
    );
  }

  if (authStatus === "out") {
    return (
      <div className="min-h-screen p-4">
        <div className="mx-auto w-full max-w-md">
          <AuthPage />
        </div>
      </div>
    );
  }

  return <AuthedApp user={authUser} />;
}